import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Route, Routes, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import BranchView from "./components/BranchView";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "context/authContext";
import { getCompanies } from "services/api";
import CompanyModel from "models/companyModel";
import LoadingScreen from "components/Loading";
import { getBranches } from "services/api";
import BranchModel from "models/branchModel";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { Button, Grid, Stack } from "@mui/material";
import { getJobs } from "services/api";
import JobModel from "models/jobModel";
import JobView from "./components/JobView";
import { getDepartments } from "services/api";
import DepartmentModel from "models/departmentModel";
import { getEmployees } from "services/api";
import EmployeesView from "./components/EmployeesView";
import { getJobToBranch } from "services/api";
import { calculateAverageLocation } from "services/utils";
import { getAreas } from "services/api";
import AreaView from "./components/AreaView";
import { useTranslation } from "react-i18next";
import { ReactFlowProvider } from "reactflow";
import { hasEditAccess } from "services/utils";
import { buttonStyles } from "services/utils";

const OrganizationPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const viewSent = location.state?.view;
  const [view, setView] = useState(viewSent ? viewSent : 0);
  const [loading, setLoading] = useState(true);
  const [updateIndex, setUpdateIndex] = useState(1);
  const [branches, setBranches] = useState();
  const [departments, setDepartments] = useState();
  const [center, setCenter] = useState(null);
  //auth
  const {
    companies,
    setCompanies,
    jobs,
    setJobs,
    setJobToBranch,
    jobToBranch,
    areas,
    setAreas,
    modules,
    employee,
  } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const tasks = [];

        // Companies
        if (!companies) {
          tasks.push(getCompanies());
        } else {
          setSelectedCompany(companies[0]);
        }

        tasks.push(getJobs());

        // JobToBranch
        if (!jobToBranch) {
          tasks.push(getJobToBranch());
        }

        // Departments, Branches, Areas (Always fetch)
        tasks.push(getDepartments());
        tasks.push(getBranches());
        tasks.push(getAreas());

        const results = await Promise.all(tasks);

        let index = 0;

        // --- Companies ---
        if (!companies) {
          const comps = results[index++];
          if (comps !== "error") {
            const companyList = comps.map((c) => new CompanyModel(c));
            setCompanies(companyList);
            if (companyList.length > 0) {
              setSelectedCompany(companyList[0]);
            }
          }
        }

        // --- Jobs ---
        const job = results[index++];
        if (job !== "error") {
          const jobList = job.map((c) => new JobModel(c));

          // Sort directly (Ascending order)
          jobList.sort((a, b) => a.jobId - b.jobId);

          setJobs(jobList);
        }
        // --- Job to Branch ---
        if (!jobToBranch) {
          const jtb = results[index++];
          if (jtb !== "error") {
            setJobToBranch(jtb);
          }
        }

        // --- Departments ---
        const deps = results[index++];
        if (deps !== "error") {
          const departmentsModel = deps.map((d) => new DepartmentModel(d));
          setDepartments(departmentsModel);
        }

        // --- Branches ---
        const br = results[index++];
        if (br !== "error") {
          const branch = br.map((b) => new BranchModel(b));
          setBranches(branch);
          setCenter(calculateAverageLocation(br));
        }

        // --- Areas ---
        const res = results[index++];
        if (res !== "error") {
          setAreas(res);
        }
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setLoading(false);
      }

      setCanEdit(hasEditAccess(modules, "Organization", employee));
    };

    fetchData();
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  // Common button styles

  const buttons = [
    { label: t("admin.dashboard.areas"), viewIndex: 0 },
    { label: t("admin.dashboard.branches"), viewIndex: 1 },
    { label: t("admin.dashboard.jobs"), viewIndex: 2 },
    { label: t("admin.dashboard.employees"), viewIndex: 3 },
  ];
  return (
    <>
      <MDBox>
        <Stack pb={2}>
          <Grid container spacing={1} sx={{ justifyContent: { xs: "center", md: "flex-start" } }}>
            {buttons.map(({ label, viewIndex }) => (
              <Grid item xs={12} sm={6} md="auto" key={viewIndex}>
                <Button
                  sx={{
                    ...buttonStyles(view === viewIndex),
                    width: { xs: "100%", md: "auto" }, // Full width on small screens, auto on large
                  }}
                  variant={view === viewIndex ? "contained" : "outlined"}
                  onClick={() => setView(viewIndex)}
                  color="primary"
                >
                  {label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Stack>
        {view == 0 ? (
          <AreaView areas={areas} center={center} setAreas={setAreas} canEdit={canEdit} />
        ) : view == 1 ? (
          <BranchView
            branches={branches}
            companyId={selectedCompany?.companyId}
            companies={companies}
            updateBranches={setBranches}
            company={selectedCompany}
            jobs={jobs}
            setJobToBranch={setJobToBranch}
            jobToBranch={jobToBranch}
            center={center}
            areas={areas}
            canEdit={canEdit}
          />
        ) : view == 2 ? (
          <ReactFlowProvider>
            <JobView
              company={selectedCompany}
              jobs={jobs}
              departments={departments}
              setJobs={setJobs}
              setDepartments={setDepartments}
              setJobToBranch={setJobToBranch}
              jobToBranch={jobToBranch}
              branches={branches}
              canEdit={canEdit}
            />
          </ReactFlowProvider>
        ) : (
          <EmployeesView
            jobs={jobs}
            branches={branches}
            departments={departments}
            canEdit={canEdit}
          />
        )}
      </MDBox>
    </>
  );
};

export default OrganizationPage;
